import React, { useState, useEffect } from 'react';
import { Eye, FileText, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, Filter, Star } from 'lucide-react';
import PRApprovalModal from '../Modal/PRApprovalModal';
import API_BASE_URL from "../config";

const PRApproval = () => {
  const [prHeaders, setPrHeaders] = useState([]);
  const [filteredPRs, setFilteredPRs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPR, setSelectedPR] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [prRatings, setPrRatings] = useState({});
  const [loadingRatings, setLoadingRatings] = useState({});

  // ✅ Users map: { userId: fullName }
  const [usersMap, setUsersMap] = useState({});

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(7);
  const [sortField, setSortField] = useState('status');
  const [sortDirection, setSortDirection] = useState('asc');

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchPRHeaders();
  }, []);

  useEffect(() => {
    filterAndSortPRs();
  }, [searchTerm, prHeaders, statusFilter, typeFilter, sortField, sortDirection]);

  // ✅ Fetch all users and build a map { userId: "First Last" }
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Users`);
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      const map = {};
      data.forEach(u => {
        // Support both id formats
        const key = u.userId || u.id;
        const fullName = u.fullName
          || `${u.firstName || ''} ${u.lastName || ''}`.trim()
          || u.username
          || `User ${key}`;
        map[key] = fullName;
      });
      setUsersMap(map);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
const fetchPRHeaders = async () => {
  setLoading(true);
  try {
    const res = await fetch(`${API_BASE_URL}api/PR_Header`);
    const data = await res.json();
    const normalized = data
      .map(pr => ({ ...pr, status: pr.status ?? 0 }))
      .filter(pr => pr.status !== 0); // ❌ Hide Draft  
    setPrHeaders(normalized);
    setFilteredPRs(normalized);

      // Fetch ratings for all PRs
      data.forEach(pr => {
        fetchPRRating(pr.id);
      });
    } catch (error) {
      console.error('Error fetching PR headers:', error);
      alert('Error loading PR list');
    } finally {
      setLoading(false);
    }
  };

  const fetchPRRating = async (prId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/PR_Header/${prId}/rate`);
      if (res.ok) {
        const data = await res.json();
        setPrRatings(prev => ({
          ...prev,
          [prId]: data.rate || 0
        }));
      }
    } catch (error) {
      console.error(`Error fetching rating for PR ${prId}:`, error);
    }
  };

  const updatePRRating = async (prId, rating) => {
    setLoadingRatings(prev => ({ ...prev, [prId]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}api/PR_Header/${prId}/rate`, {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rating)
      });

      if (res.ok) {
        const data = await res.json();
        setPrRatings(prev => ({
          ...prev,
          [prId]: data.rate
        }));
      } else {
        alert('Failed to update rating');
      }
    } catch (error) {
      console.error(`Error updating rating for PR ${prId}:`, error);
      alert('Error updating rating');
    } finally {
      setLoadingRatings(prev => ({ ...prev, [prId]: false }));
    }
  };

  const filterAndSortPRs = () => {
    let filtered = [...prHeaders];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(pr =>
        pr.prNo?.toLowerCase().includes(term) ||
        pr.remarks?.toLowerCase().includes(term) ||
        // ✅ Also search by resolved user name
        (usersMap[pr.createdBy] || '').toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      const targetStatus = parseInt(statusFilter);
      filtered = filtered.filter(pr => {
        const prStatus = (pr.status === null || pr.status === undefined) ? 0 : pr.status;
        return prStatus === targetStatus;
      });
    }

    // ✅ FIX: use pr.type (not pr.prType) for type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(pr => pr.type === typeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle dates
      if (sortField === 'dateNeeded' || sortField === 'creationDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle strings
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredPRs(filtered);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 opacity-50" />;
    }
    return sortDirection === 'asc' ?
      <ArrowUp className="w-4 h-4" /> :
      <ArrowDown className="w-4 h-4" />;
  };

  const openViewModal = (pr) => {
    setSelectedPR(pr);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedPR(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
      1: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
      2: { label: 'Approved', color: 'bg-green-100 text-green-700' },
      3: { label: 'Rejected', color: 'bg-red-100 text-red-700' }
    };
    const s = (status === null || status === undefined) ? 0 : status;
    const statusInfo = statusMap[s] ?? statusMap[0]; return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const RatingStars = ({ prId, currentRating }) => {
    const [hoveredRating, setHoveredRating] = useState(0);
    const isLoading = loadingRatings[prId];
    const displayRating = hoveredRating || currentRating || 0;

    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => !isLoading && updatePRRating(prId, star)}
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
            disabled={isLoading}
            className={`transition-all ${isLoading ? 'cursor-wait opacity-50' : 'cursor-pointer hover:scale-110'}`}
          >
            <Star
              className={`w-5 h-5 ${star <= displayRating
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-none text-gray-300'
                }`}
            />
          </button>
        ))}
        {isLoading && (
          <span className="ml-2 text-xs text-gray-500">Updating...</span>
        )}
      </div>
    );
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPRs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPRs.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handleApprove = async (pr) => {
    console.log('Approving PR:', pr);
    closeViewModal();
  };

  const handleReject = async (pr) => {
    console.log('Rejecting PR:', pr);
    closeViewModal();
  };

  // ✅ FIX: Get unique types from pr.type (not pr.prType)
  const uniquePRTypes = [...new Set(prHeaders.map(pr => pr.type))].filter(Boolean);

  return (
    <div className="min-h-screen from-blue-50 via-slate-50 to-blue-100 p-6">
      <div className="max-w-8xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 shadow-lg mb-6 backdrop-blur-sm bg-opacity-95">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7" />
            Purchase Request Approval
          </h1>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg p-6 mb-6 border border-white/20">
          <div className="flex flex-col gap-4">
            {/* Search Bar */}
            <div className="flex gap-4 items-center">
              <Search className="w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by PR Number, Remarks, or Requestor name..."
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg bg-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${showFilters ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
            </div>

            {/* Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="all">All Status</option>
                    <option value="0">Draft</option>
                    <option value="1">Pending</option>
                    <option value="2">Approved</option>
                    <option value="3">Rejected</option>
                  </select>
                </div>

                {/* ✅ FIX: Type filter now uses pr.type values */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Type</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="all">All Types</option>
                    {uniquePRTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Items Per Page</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="5">5 per page</option>
                    <option value="7">7 per page</option>
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                </div>
              </div>
            )}

            {/* Active Filters Summary */}
            {(statusFilter !== 'all' || typeFilter !== 'all') && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-600">Active Filters:</span>
                {statusFilter !== 'all' && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    Status: {['Draft', 'Pending', 'Approved', 'Rejected'][statusFilter]}
                    <button onClick={() => setStatusFilter('all')} className="ml-2 hover:text-blue-900">×</button>
                  </span>
                )}
                {typeFilter !== 'all' && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    Type: {typeFilter}
                    <button onClick={() => setTypeFilter('all')} className="ml-2 hover:text-blue-900">×</button>
                  </span>
                )}
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                  className="text-xs text-red-600 hover:text-red-700 font-medium"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>

        {/* PR Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          {/* Results Info */}
          <div className="px-6 py-3 bg-gray-50/50 border-b border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredPRs.length === 0 ? 0 : indexOfFirstItem + 1}</span> to{' '}
              <span className="font-semibold text-gray-900">{Math.min(indexOfLastItem, filteredPRs.length)}</span> of{' '}
              <span className="font-semibold text-gray-900">{filteredPRs.length}</span> results
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
              <p className="text-gray-500 mt-4 text-sm font-medium">Loading PR list...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                    <tr>
                      <th
                        onClick={() => handleSort('prNo')}
                        className="px-6 py-4 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          PR Number
                          {getSortIcon('prNo')}
                        </div>
                      </th>
                      {/* ✅ FIX: Column header now says "Type", sorts by 'type' */}
                      <th
                        onClick={() => handleSort('type')}
                        className="px-6 py-4 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          Type
                          {getSortIcon('type')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('typeOfPurchase')}
                        className="px-6 py-4 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          Purchase Type
                          {getSortIcon('typeOfPurchase')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('dateNeeded')}
                        className="px-6 py-4 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          Date Needed
                          {getSortIcon('dateNeeded')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('createdBy')}
                        className="px-6 py-4 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          Created By
                          {getSortIcon('createdBy')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('status')}
                        className="px-6 py-4 text-left text-xs font-semibold uppercase cursor-pointer hover:bg-blue-700 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          Status
                          {getSortIcon('status')}
                        </div>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase">Remarks</th>
                      <th className="px-6 py-4 text-center text-xs font-semibold uppercase">Rate</th>
                      <th className="px-6 py-4 text-center text-xs font-semibold uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentItems.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                          No purchase requests found
                        </td>
                      </tr>
                    ) : (
                      currentItems.map((pr) => (
                        <tr key={pr.id} className="hover:bg-blue-50/50 transition-colors">
                          <td className="px-6 py-4 text-sm font-semibold text-blue-700">{pr.prNo}</td>

                          {/* ✅ FIX: pr.type instead of pr.prType */}
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {pr.type
                              ? <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">{pr.type}</span>
                              : <span className="text-gray-400">—</span>
                            }
                          </td>

                          {/* TypeOfPurchase column */}
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {pr.typeOfPurchase
                              ? <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">{pr.typeOfPurchase}</span>
                              : <span className="text-gray-400">—</span>
                            }
                          </td>

                          <td className="px-6 py-4 text-sm text-gray-700">{formatDate(pr.dateNeeded)}</td>

                          {/* ✅ FIX: Resolve createdBy to actual user name */}
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {(usersMap[pr.createdBy] || '?')[0].toUpperCase()}
                              </div>
                              <span>{usersMap[pr.createdBy] || `User ${pr.createdBy}`}</span>
                            </div>
                          </td>

                          <td className="px-6 py-4 text-sm">{getStatusBadge(pr.status)}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{pr.remarks || '—'}</td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center">
                              <RatingStars
                                prId={pr.id}
                                currentRating={prRatings[pr.id]}
                              />
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => openViewModal(pr)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all shadow-md"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredPRs.length > 0 && (
                <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
                      <span className="font-semibold text-gray-900">{totalPages}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="First Page"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Previous Page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-500">...</span>
                          ) : (
                            <button
                              key={page}
                              onClick={() => paginate(page)}
                              className={`px-4 py-2 rounded-lg font-medium transition-all ${currentPage === page
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>

                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Next Page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Last Page"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <PRApprovalModal
          isOpen={showViewModal}
          onClose={closeViewModal}
          selectedPR={selectedPR}
          onApprove={handleApprove}
          onReject={handleReject}
          getStatusBadge={getStatusBadge}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
};

export default PRApproval;